import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { initAnalytics, trackPageView, setAnalyticsRole } from "@/lib/analytics";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { PortalAuthProvider, usePortalAuth } from "@/contexts/PortalAuthContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { CampusProvider } from "@/contexts/CampusContext";
import { Toaster } from "sonner";
import DashboardLayout from "@/components/layouts/DashboardLayout";
import MaintenanceGate from "@/components/MaintenanceGate";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import QuizList from "@/pages/QuizList";
import Groups from "@/pages/Groups";
import Categories from "@/pages/Categories";
import LockedAccounts from "@/pages/LockedAccounts";
import SystemMonitoring from "@/pages/SystemMonitoring";
import AdminManagement from "@/pages/AdminManagement";
import UserManagement from "@/pages/UserManagement";
import MyQuizTakers from "@/pages/MyQuizTakers";
import AuditLogs from "@/pages/AuditLogs";
import Settings from "@/pages/Settings";
import Organizations from "@/pages/Organizations";
import OrganizationSettings from "@/pages/OrganizationSettings";
import OrganizationMembers from "@/pages/OrganizationMembers";
import Campuses from "@/pages/Campuses";

// Lazy load heavy pages for better performance (40% smaller initial bundle)
const QuizBuilder = lazy(() => import("@/pages/QuizBuilder"));
const QuizTaker = lazy(() => import("@/pages/QuizTaker"));
const Reports = lazy(() => import("@/pages/Reports"));
const Analyse = lazy(() => import("@/pages/Analyse"));
const AtRiskStudents = lazy(() => import("@/pages/AtRiskStudents"));
const ManagementReportConfigs = lazy(() => import("@/pages/ManagementReportConfigs"));
const Results = lazy(() => import("@/pages/Results"));
const Sequences = lazy(() => import("@/pages/Sequences"));
const SequenceBuilder = lazy(() => import("@/pages/SequenceBuilder"));
const Courses = lazy(() => import("@/pages/Courses"));
const CourseBuilder = lazy(() => import("@/pages/CourseBuilder"));
const SequenceLanding = lazy(() => import("@/pages/SequenceLanding"));
const SequenceRunner = lazy(() => import("@/pages/SequenceRunner"));
const PortalLogin = lazy(() => import("@/pages/portal/Login"));
const PortalDashboard = lazy(() => import("@/pages/portal/Dashboard"));
const PortalCoursePlayer = lazy(() => import("@/pages/portal/CoursePlayer"));
const PortalTheme = lazy(() => import("@/pages/PortalTheme"));

// Loading spinner component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-sm text-slate-600">Loading...</p>
    </div>
  </div>
);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Portal-side guard. Independent from admin ProtectedRoute so a signed-in
// admin browsing /portal/* doesn't get auto-treated as authenticated.
function PortalProtectedRoute({ children }) {
  const { user, loading } = usePortalAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/portal/login" replace />;
  return children;
}

// Fires a GA4 page_view on every route change and tags the session with a
// coarse role (admin/superadmin/participant — never identity). No-op unless
// REACT_APP_GA_MEASUREMENT_ID is set in a production build.
function AnalyticsTracker() {
  const location = useLocation();
  const { user: adminUser } = useAuth();
  const { user: portalUser } = usePortalAuth();

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    const role = adminUser?.role || (portalUser ? "participant" : null);
    setAnalyticsRole(role);
  }, [adminUser, portalUser]);

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return null;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return null;

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Org-scoped login route (e.g., /acme-corp/login) */}
        <Route path="/:orgSlug/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
        {/* Legacy login route (backward compatible with ?org= query param) */}
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout><Dashboard /></DashboardLayout></ProtectedRoute>} />
        <Route path="/quizzes" element={<ProtectedRoute><DashboardLayout><QuizList /></DashboardLayout></ProtectedRoute>} />
        <Route path="/quizzes/new" element={<ProtectedRoute><DashboardLayout><QuizBuilder /></DashboardLayout></ProtectedRoute>} />
        <Route path="/quizzes/:id/edit" element={<ProtectedRoute><DashboardLayout><QuizBuilder /></DashboardLayout></ProtectedRoute>} />
        <Route path="/groups" element={<ProtectedRoute><DashboardLayout><Groups /></DashboardLayout></ProtectedRoute>} />
        <Route path="/categories" element={<ProtectedRoute><DashboardLayout><Categories /></DashboardLayout></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><DashboardLayout><Reports /></DashboardLayout></ProtectedRoute>} />
        <Route path="/analyse" element={<ProtectedRoute><DashboardLayout><Analyse /></DashboardLayout></ProtectedRoute>} />
        <Route path="/at-risk-students" element={<ProtectedRoute><DashboardLayout><AtRiskStudents /></DashboardLayout></ProtectedRoute>} />
        <Route path="/management-report-configs" element={<ProtectedRoute><DashboardLayout><ManagementReportConfigs /></DashboardLayout></ProtectedRoute>} />
        <Route path="/locked-accounts" element={<ProtectedRoute><DashboardLayout><LockedAccounts /></DashboardLayout></ProtectedRoute>} />
        <Route path="/system-monitoring" element={<ProtectedRoute><DashboardLayout><SystemMonitoring /></DashboardLayout></ProtectedRoute>} />
        <Route path="/admin-management" element={<ProtectedRoute><DashboardLayout><AdminManagement /></DashboardLayout></ProtectedRoute>} />
        <Route path="/user-management" element={<ProtectedRoute><DashboardLayout><UserManagement /></DashboardLayout></ProtectedRoute>} />
        <Route path="/my-quiz-takers" element={<ProtectedRoute><DashboardLayout><MyQuizTakers /></DashboardLayout></ProtectedRoute>} />
        <Route path="/audit-logs" element={<ProtectedRoute><DashboardLayout><AuditLogs /></DashboardLayout></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><DashboardLayout><Settings /></DashboardLayout></ProtectedRoute>} />
        <Route path="/organizations" element={<ProtectedRoute><DashboardLayout><Organizations /></DashboardLayout></ProtectedRoute>} />
        <Route path="/portal-theme" element={<ProtectedRoute><DashboardLayout><PortalTheme /></DashboardLayout></ProtectedRoute>} />
        <Route path="/organization-settings" element={<ProtectedRoute><DashboardLayout><OrganizationSettings /></DashboardLayout></ProtectedRoute>} />
        <Route path="/organization-members" element={<ProtectedRoute><DashboardLayout><OrganizationMembers /></DashboardLayout></ProtectedRoute>} />
        <Route path="/campuses" element={<ProtectedRoute><DashboardLayout><Campuses /></DashboardLayout></ProtectedRoute>} />
        <Route path="/courses" element={<ProtectedRoute><DashboardLayout><Courses /></DashboardLayout></ProtectedRoute>} />
        <Route path="/courses/new" element={<ProtectedRoute><DashboardLayout><CourseBuilder /></DashboardLayout></ProtectedRoute>} />
        <Route path="/courses/:courseId" element={<ProtectedRoute><DashboardLayout><CourseBuilder /></DashboardLayout></ProtectedRoute>} />
        <Route path="/sequences" element={<ProtectedRoute><DashboardLayout><Sequences /></DashboardLayout></ProtectedRoute>} />
        <Route path="/sequences/new" element={<ProtectedRoute><DashboardLayout><SequenceBuilder /></DashboardLayout></ProtectedRoute>} />
        <Route path="/sequences/:id/edit" element={<ProtectedRoute><DashboardLayout><SequenceBuilder /></DashboardLayout></ProtectedRoute>} />
        <Route path="/quiz/:slug" element={<QuizTaker />} />
        <Route path="/quiz/:slug/results/:submissionId" element={<Results />} />
        <Route path="/s/:slug" element={<SequenceLanding />} />
        <Route path="/s/:slug/attempt" element={<SequenceRunner />} />

        {/* Student Portal — separate auth context (portal_token in localStorage) */}
        <Route path="/portal/login" element={<PortalLogin />} />
        <Route path="/portal" element={<PortalProtectedRoute><PortalDashboard /></PortalProtectedRoute>} />
        <Route path="/portal/courses/:courseId" element={<PortalProtectedRoute><PortalCoursePlayer /></PortalProtectedRoute>} />

        <Route path="/" element={<Navigate to={user ? "/dashboard" : "/login"} replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <MaintenanceGate>
      <BrowserRouter>
        <AuthProvider>
          <PortalAuthProvider>
            <OrganizationProvider>
              <CampusProvider>
                <AnalyticsTracker />
                <AppRoutes />
                <Toaster position="top-right" richColors closeButton />
              </CampusProvider>
            </OrganizationProvider>
          </PortalAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </MaintenanceGate>
  );
}
